/**
 * @description
 * Stripe subscription service for managing subscriptions and plans.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { StripeSubscription } from "./types";

/**
 * Stripe subscription service
 */
export class StripeSubscriptionService extends BaseAPIClient {
  /**
   * Get subscriptions for client
   */
  async getSubscriptions(clientId: string): Promise<StripeSubscription[]> {
    const data = await this.request<{ subscriptions: StripeSubscription[] }>(`/stripe/subscriptions/${clientId}`);
    return data.subscriptions;
  }

  /**
   * Create subscription for client
   */
  async createSubscription(clientId: string, data: {
    items: Array<{ priceId: string; quantity?: number }>;
    trialPeriodDays?: number;
  }): Promise<StripeSubscription> {
    const response = await this.request<{ subscription: StripeSubscription }>('/stripe/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ clientId, ...data }),
    });
    return response.subscription;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId: string): Promise<void> {
    await this.request(`/stripe/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Delete subscription
   */
  async deleteSubscription(id: string): Promise<void> {
    await this.request(`/stripe/subscriptions/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get subscription plans
   */
  async getSubscriptionPlans(): Promise<Array<{
    id: string;
    name: string;
    description: string;
    amount: number;
    interval: 'month' | 'year';
  }>> {
    const response = await this.request<{ subscriptions: any[] }>('/stripe/subscriptions');
    return response.subscriptions;
  }

  /**
   * Create subscription plan
   */
  async createSubscriptionPlan(data: {
    name: string;
    description: string;
    amount: number;
    interval: 'month' | 'year';
  }): Promise<any> {
    const response = await this.request<{ subscription: any }>('/stripe/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.subscription;
  }

  /**
   * Update subscription
   */
  async updateSubscription(id: string, data: Partial<{
    name: string;
    description: string;
    amount: number;
    interval: 'month' | 'year';
    isActive: boolean;
  }>): Promise<any> {
    const response = await this.request<{ subscription: any }>(`/stripe/subscriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.subscription;
  }
}

// Export singleton instance
export const stripeSubscriptionService = new StripeSubscriptionService();
